import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './styles.module.scss'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { SubmitButton } from '../../reusable'

// Available introducers for selection
const AVAILABLE_INTRODUCERS = [
    { id: 'PCP Sentinel', label: 'PCP Sentinel' },
    { id: 'ADL', label: 'ADL' },
    { id: 'Mail Referral', label: 'Mail Referral' },
    { id: 'SalesOne', label: 'SalesOne' },
    { id: 'PCPMO26', label: 'PCPMO26' },
]

interface ClaimRecord {
    claim_ref: string
    client_name: string
    email: string
    lender: string
    loan_value: number
    claim_start_date: string
    commission_value: number
    daily_stat_interest_percentage: number
    daily_stat_interest_value: number
    total_days: number
    total_statutory_interest: number
    claim_value: number
    source: string
    client_total_value?: number
    client_claim_count?: number
}

interface Metrics {
    totalBookValue: number
    averageClaimValue: number
    averageClientValue: number
    highestClaimValue: number
    highestClaimRef: string | null
    highestClientValue: number
    highestClientEmail: string | null
    lowestClaimValue: number
    lowestClaimRef: string | null
    lowestClientValue: number
    lowestClientEmail: string | null
    totalStatutoryInterest: number
    totalCommissionValue: number
    totalClaims: number
    totalEligibleClients: number
}

interface Pagination {
    page: number
    pageSize: number
    totalRecords: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
}

interface DbStats {
    [key: string]: {
        total: number
        validClaims: number
        eligibleClients: number
        clientsBelowMinimum: number
    }
}

interface ClientStats {
    totalClients: number
    eligibleClients: number
    clientsBelowMinimum: number
}

interface ExclusionStats {
    unapprovedLender: number
    duplicatePolicyNumber: number
    duplicateEmail: number
    noPolicyNumber: number
}

export const ClaimValuationsView = () => {
    const navigate = useNavigate()
    const [isAuthorised, setIsAuthorised] = useState<boolean>(false)
    const [commissionPercentage, setCommissionPercentage] = useState<string>('')
    const [statutoryInterestPercentage, setStatutoryInterestPercentage] = useState<string>('')
    const [minimumClientValue, setMinimumClientValue] = useState<string>('')
    const [selectedDate, setSelectedDate] = useState<string>('')
    const [customDate, setCustomDate] = useState<string>('')
    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const [isCalculationExpanded, setIsCalculationExpanded] = useState<boolean>(true)
    const [isFormExpanded, setIsFormExpanded] = useState<boolean>(true)
    const [hasCalculated, setHasCalculated] = useState<boolean>(false)
    const [claimRecords, setClaimRecords] = useState<ClaimRecord[]>([])
    const [metrics, setMetrics] = useState<Metrics | null>(null)
    const [pagination, setPagination] = useState<Pagination | null>(null)
    const [dbStats, setDbStats] = useState<DbStats | null>(null)
    const [clientStats, setClientStats] = useState<ClientStats | null>(null)
    const [exclusionStats, setExclusionStats] = useState<ExclusionStats | null>(null)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [isSearching, setIsSearching] = useState<boolean>(false)
    const [searchResults, setSearchResults] = useState<ClaimRecord[] | null>(null)
    const [searchPagination, setSearchPagination] = useState<Pagination | null>(null)
    const [isExporting, setIsExporting] = useState<boolean>(false)

    // Introducer selection state
    const [introducerSelectionMode, setIntroducerSelectionMode] = useState<'all' | 'selected'>('all')
    const [selectedIntroducers, setSelectedIntroducers] = useState<string[]>(AVAILABLE_INTRODUCERS.map((i) => i.id))

    const PAGE_SIZE = 100

    // Check authentication on page load
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await api({ url: '/claim-valuations-page', data: {} })
                if (response.status === 200) {
                    setIsAuthorised(true)
                }
            } catch (error: any) {
                console.error('Authentication check failed:', error)
                Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK', () => {
                    window.localStorage.removeItem('access')
                    navigate('/login')
                })
            }
        }
        checkAuth()
    }, [navigate])

    const datePresets = [
        { label: 'Today', value: 'today' },
        { label: '1 April 2026', value: '2026-04-01' },
        { label: '1 December 2025', value: '2025-12-01' },
        { label: 'Custom Date', value: 'custom' },
    ]

    // Get the effective introducers to use for the API call
    const getEffectiveIntroducers = (): string[] => {
        if (introducerSelectionMode === 'all') {
            return AVAILABLE_INTRODUCERS.map((i) => i.id)
        }
        return selectedIntroducers
    }

    // Handle introducer checkbox toggle
    const handleIntroducerToggle = (introducerId: string) => {
        setSelectedIntroducers((prev) => {
            if (prev.includes(introducerId)) {
                // Don't allow deselecting if it's the last one
                if (prev.length === 1) return prev
                return prev.filter((id) => id !== introducerId)
            }
            return [...prev, introducerId]
        })
    }

    // Handle "Select All" / "Deselect All" for introducers
    const handleSelectAllIntroducers = () => {
        setSelectedIntroducers(AVAILABLE_INTRODUCERS.map((i) => i.id))
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (!commissionPercentage || parseFloat(commissionPercentage) < 0 || parseFloat(commissionPercentage) > 100) {
            newErrors.commission = 'Commission must be between 0 and 100'
        }

        if (
            !statutoryInterestPercentage ||
            parseFloat(statutoryInterestPercentage) < 0 ||
            parseFloat(statutoryInterestPercentage) > 100
        ) {
            newErrors.statutory = 'Statutory interest must be between 0 and 100'
        }

        if (minimumClientValue && parseFloat(minimumClientValue) < 0) {
            newErrors.minimumClientValue = 'Minimum client value cannot be negative'
        }

        if (!selectedDate) {
            newErrors.date = 'Please select a date'
        }

        if (selectedDate === 'custom' && !customDate) {
            newErrors.customDate = 'Please select a custom date'
        }

        // Validate introducer selection
        if (introducerSelectionMode === 'selected' && selectedIntroducers.length === 0) {
            newErrors.introducers = 'Please select at least one introducer'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const getDateValue = () => {
        if (selectedDate === 'today') {
            return new Date().toISOString()
        } else if (selectedDate === 'custom') {
            return new Date(customDate).toISOString()
        } else {
            return new Date(selectedDate).toISOString()
        }
    }

    const getDisplayDate = () => {
        if (selectedDate === 'today') {
            return new Date().toLocaleDateString('en-GB')
        } else if (selectedDate === 'custom') {
            return new Date(customDate).toLocaleDateString('en-GB')
        } else {
            const preset = datePresets.find((p) => p.value === selectedDate)
            return preset ? preset.label : selectedDate
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value)
    }

    const formatPercentage = (value: number, decimals: number = 6) => {
        return `${value.toFixed(decimals)}%`
    }

    const formatNumber = (value: number) => {
        return new Intl.NumberFormat('en-GB').format(value)
    }

    const fetchClaimValuations = useCallback(
        async (page: number, append: boolean = false) => {
            try {
                const apiResponse = await api({
                    url: '/claim-valuations',
                    data: {
                        commissionPercentage: parseFloat(commissionPercentage),
                        statutoryInterestPercentage: parseFloat(statutoryInterestPercentage),
                        minimumClientValue: minimumClientValue ? parseFloat(minimumClientValue) : 0,
                        selectedDate: getDateValue(),
                        introducers: getEffectiveIntroducers(),
                        page,
                        pageSize: PAGE_SIZE,
                    },
                })

                if (apiResponse.data.success) {
                    if (append) {
                        setClaimRecords((prev) => [...prev, ...(apiResponse.data.records || [])])
                    } else {
                        setClaimRecords(apiResponse.data.records || [])
                    }
                    setMetrics(apiResponse.data.metrics || null)
                    setPagination(apiResponse.data.pagination || null)
                    setDbStats(apiResponse.data.dbStats || null)
                    setClientStats(apiResponse.data.clientStats || null)
                    setExclusionStats(apiResponse.data.exclusionStats || null)
                    setCurrentPage(page)
                    return true
                } else {
                    Report.failure('Error', apiResponse.data.message || 'Failed to calculate claim values', 'OKAY')
                    return false
                }
            } catch (error: any) {
                console.error('Claim valuation error:', error)
                Report.failure('Error', error.message || 'Failed to calculate claim values', 'OKAY')
                return false
            }
        },
        [
            commissionPercentage,
            statutoryInterestPercentage,
            minimumClientValue,
            selectedDate,
            customDate,
            introducerSelectionMode,
            selectedIntroducers,
        ],
    )

    const handleSubmit = async (e: React.MouseEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            Report.failure('Validation Error', 'Please correct the errors in the form', 'OKAY')
            return
        }

        Loading.circle('Calculating client values...')
        setClaimRecords([])
        setCurrentPage(1)

        const success = await fetchClaimValuations(1, false)

        Loading.remove()

        if (success) {
            setHasCalculated(true)
            setIsFormExpanded(false)
        }
    }

    const handleLoadMore = async () => {
        if (!pagination?.hasNextPage || isLoadingMore) return

        setIsLoadingMore(true)
        await fetchClaimValuations(currentPage + 1, true)
        setIsLoadingMore(false)
    }

    const [isCompactMode, setIsCompactMode] = useState<boolean>(false)

    const handleEditForm = () => {
        setIsFormExpanded(true)
        setIsCompactMode(true) // Show compact form when editing
    }

    const handleSearch = async () => {
        if (!searchTerm.trim() || searchTerm.trim().length < 2) {
            Report.failure('Search Error', 'Search term must be at least 2 characters', 'OKAY')
            return
        }

        setIsSearching(true)
        try {
            const apiResponse = await api({
                url: '/claim-valuations-search',
                data: {
                    searchTerm: searchTerm.trim(),
                    commissionPercentage: parseFloat(commissionPercentage),
                    statutoryInterestPercentage: parseFloat(statutoryInterestPercentage),
                    minimumClientValue: minimumClientValue ? parseFloat(minimumClientValue) : 0,
                    selectedDate: getDateValue(),
                    introducers: getEffectiveIntroducers(),
                    page: 1,
                    pageSize: PAGE_SIZE,
                },
            })

            if (apiResponse.data.success) {
                setSearchResults(apiResponse.data.records || [])
                setSearchPagination(apiResponse.data.pagination || null)
            } else {
                Report.failure('Search Error', apiResponse.data.message || 'Failed to search claims', 'OKAY')
            }
        } catch (error: any) {
            console.error('Search error:', error)
            Report.failure('Search Error', error.message || 'Failed to search claims', 'OKAY')
        } finally {
            setIsSearching(false)
        }
    }

    const handleClearSearch = () => {
        setSearchTerm('')
        setSearchResults(null)
        setSearchPagination(null)
    }

    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleExport = async () => {
        setIsExporting(true)
        try {
            const apiResponse = await api({
                url: '/claim-valuations-export',
                data: {
                    commissionPercentage: parseFloat(commissionPercentage),
                    statutoryInterestPercentage: parseFloat(statutoryInterestPercentage),
                    minimumClientValue: minimumClientValue ? parseFloat(minimumClientValue) : 0,
                    selectedDate: getDateValue(),
                    introducers: getEffectiveIntroducers(),
                },
            })

            if (apiResponse.data.success && apiResponse.data.csv) {
                // Create a blob and download the CSV
                const blob = new Blob([apiResponse.data.csv], { type: 'text/csv;charset=utf-8;' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                const filename = `claim-valuations-${new Date().toISOString().split('T')[0]}.csv`
                link.setAttribute('download', filename)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } else {
                Report.failure('Export Error', apiResponse.data.message || 'Failed to export claims', 'OKAY')
            }
        } catch (error: any) {
            console.error('Export error:', error)
            Report.failure('Export Error', error.message || 'Failed to export claims', 'OKAY')
        } finally {
            setIsExporting(false)
        }
    }

    // Use search results if available, otherwise use all claim records
    const displayRecords = searchResults !== null ? searchResults : claimRecords
    const displayPagination = searchResults !== null ? searchPagination : pagination

    // Show loading state while checking authentication
    if (!isAuthorised) {
        return (
            <div className={styles.claimValuationsContainer}>
                <div className={styles.formCard}>
                    <div className={styles.loadingState}>
                        <p>Verifying access...</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.claimValuationsContainer}>
            <div className={styles.formCard}>
                {/* Collapsed Form Summary */}
                {hasCalculated && !isFormExpanded && (
                    <div className={styles.collapsedSummary}>
                        <div className={styles.summaryHeader}>
                            <h3>Configuration Applied</h3>
                            <button className={styles.editButton} onClick={handleEditForm}>
                                Edit Values
                            </button>
                        </div>
                        <div className={styles.summaryValues}>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Commission</span>
                                <span className={styles.summaryValue}>{commissionPercentage}%</span>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Statutory Interest</span>
                                <span className={styles.summaryValue}>{statutoryInterestPercentage}%</span>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Min Client Value</span>
                                <span className={styles.summaryValue}>
                                    {minimumClientValue ? formatCurrency(parseFloat(minimumClientValue)) : '£0.00'}
                                </span>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Calculation Date</span>
                                <span className={styles.summaryValue}>{getDisplayDate()}</span>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Introducers</span>
                                <span className={styles.summaryValue}>
                                    {introducerSelectionMode === 'all'
                                        ? 'All'
                                        : selectedIntroducers.length === AVAILABLE_INTRODUCERS.length
                                          ? 'All'
                                          : `${selectedIntroducers.length} selected`}
                                </span>
                            </div>
                        </div>
                        {/* Overall Stats Summary */}
                        {clientStats && metrics && (
                            <div className={styles.overallStats}>
                                <div className={styles.statHighlight}>
                                    <span className={styles.statNumber}>
                                        {formatNumber(clientStats.eligibleClients)}
                                    </span>
                                    <span className={styles.statLabel}>Eligible Clients</span>
                                </div>
                                <div className={styles.statHighlight}>
                                    <span className={styles.statNumber}>{formatNumber(metrics.totalClaims)}</span>
                                    <span className={styles.statLabel}>Total Claims</span>
                                </div>
                                <div className={styles.statHighlight}>
                                    <span className={styles.statNumber}>{formatCurrency(metrics.totalBookValue)}</span>
                                    <span className={styles.statLabel}>Total Book Value</span>
                                </div>
                            </div>
                        )}
                        {/* Per-Introducer Breakdown */}
                        {dbStats && (
                            <div className={styles.dbStatsRow}>
                                <span className={styles.dbStatsTitle}>Breakdown by Introducer:</span>
                                <div className={styles.dbStatsGrid}>
                                    {Object.entries(dbStats).map(([name, stats]) => (
                                        <div key={name} className={styles.dbStatCard}>
                                            <span className={styles.dbStatName}>{name}</span>
                                            <div className={styles.dbStatDetails}>
                                                <span>{formatNumber(stats.eligibleClients)} clients</span>
                                                <span>{formatNumber(stats.validClaims)} claims</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {/* Exclusion Stats */}
                        {exclusionStats && (
                            <div className={styles.exclusionStatsRow}>
                                <span className={styles.dbStatsTitle}>Claims Excluded:</span>
                                <div className={styles.exclusionStatsGrid}>
                                    {exclusionStats.unapprovedLender > 0 && (
                                        <span className={styles.exclusionStat}>
                                            Unapproved Lender: {formatNumber(exclusionStats.unapprovedLender)}
                                        </span>
                                    )}
                                    {exclusionStats.duplicatePolicyNumber > 0 && (
                                        <span className={styles.exclusionStat}>
                                            Duplicate Policy: {formatNumber(exclusionStats.duplicatePolicyNumber)}
                                        </span>
                                    )}
                                    {exclusionStats.duplicateEmail > 0 && (
                                        <span className={styles.exclusionStat}>
                                            Duplicate Email: {formatNumber(exclusionStats.duplicateEmail)}
                                        </span>
                                    )}
                                    {exclusionStats.noPolicyNumber > 0 && (
                                        <span className={styles.exclusionStat}>
                                            No Policy Number: {formatNumber(exclusionStats.noPolicyNumber)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Expanded Form */}
                {isFormExpanded && (
                    <>
                        {/* Compact Edit Form */}
                        {isCompactMode ? (
                            <>
                                <div className={styles.compactFormHeader}>
                                    <h3>Edit Configuration</h3>
                                    <button
                                        className={styles.cancelButton}
                                        onClick={() => {
                                            setIsFormExpanded(false)
                                            setIsCompactMode(false)
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className={styles.compactForm}>
                                    {/* Compact Introducer Selection */}
                                    <div className={styles.compactFormGroup}>
                                        <label>Introducers</label>
                                        <div className={styles.compactDatePresets}>
                                            <button
                                                type="button"
                                                className={`${styles.compactPresetButton} ${
                                                    introducerSelectionMode === 'all' ? styles.active : ''
                                                }`}
                                                onClick={() => setIntroducerSelectionMode('all')}
                                            >
                                                All
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.compactPresetButton} ${
                                                    introducerSelectionMode === 'selected' ? styles.active : ''
                                                }`}
                                                onClick={() => setIntroducerSelectionMode('selected')}
                                            >
                                                Select
                                            </button>
                                        </div>
                                        {introducerSelectionMode === 'selected' && (
                                            <div className={styles.compactIntroducerGrid}>
                                                {AVAILABLE_INTRODUCERS.map((introducer) => (
                                                    <label
                                                        key={introducer.id}
                                                        className={styles.compactIntroducerCheckbox}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIntroducers.includes(introducer.id)}
                                                            onChange={() => handleIntroducerToggle(introducer.id)}
                                                        />
                                                        <span>{introducer.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.compactFormGroup}>
                                        <label htmlFor="commission">Commission %</label>
                                        <input
                                            id="commission"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={commissionPercentage}
                                            onChange={(e) => setCommissionPercentage(e.target.value)}
                                            className={errors.commission ? styles.error : ''}
                                            placeholder="e.g. 10"
                                        />
                                    </div>
                                    <div className={styles.compactFormGroup}>
                                        <label htmlFor="statutory">Statutory Interest %</label>
                                        <input
                                            id="statutory"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={statutoryInterestPercentage}
                                            onChange={(e) => setStatutoryInterestPercentage(e.target.value)}
                                            className={errors.statutory ? styles.error : ''}
                                            placeholder="e.g. 8"
                                        />
                                    </div>
                                    <div className={styles.compactFormGroup}>
                                        <label htmlFor="minClientValue">Minimum Client Value (£)</label>
                                        <input
                                            id="minClientValue"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={minimumClientValue}
                                            onChange={(e) => setMinimumClientValue(e.target.value)}
                                            className={errors.minimumClientValue ? styles.error : ''}
                                            placeholder="e.g. 500"
                                        />
                                    </div>
                                    <div className={styles.compactFormGroup}>
                                        <label htmlFor="date">Calculation Date</label>
                                        <div className={styles.compactDatePresets}>
                                            {datePresets.map((preset) => (
                                                <button
                                                    key={preset.value}
                                                    type="button"
                                                    className={`${styles.compactPresetButton} ${
                                                        selectedDate === preset.value ? styles.active : ''
                                                    }`}
                                                    onClick={() => setSelectedDate(preset.value)}
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                        </div>
                                        {selectedDate === 'custom' && (
                                            <input
                                                type="date"
                                                value={customDate}
                                                onChange={(e) => setCustomDate(e.target.value)}
                                                className={`${styles.compactDateInput} ${
                                                    errors.customDate ? styles.error : ''
                                                }`}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className={styles.compactSubmitWrapper}>
                                    <SubmitButton text="Recalculate" btnType="primary" onClick={handleSubmit} />
                                </div>
                            </>
                        ) : (
                            /* Full Form */
                            <>
                                <div className={styles.formHeader}>
                                    <h3>Client Valuation Configuration</h3>
                                    <p className={styles.description}>
                                        Configure the calculation parameters that will be applied to client valuations.
                                        Select which introducers to include and set the valuation criteria. The claim
                                        value is calculated as:{' '}
                                        <strong>(total statutory interest + commission value)</strong> or{' '}
                                        <strong>
                                            ((number of days × daily stat interest value) + commission value)
                                        </strong>
                                        .
                                    </p>
                                </div>

                                <form className={styles.form}>
                                    {/* Introducer Selection */}
                                    <div className={styles.formGroup}>
                                        <label>
                                            Introducer Selection <span className={styles.required}>*</span>
                                        </label>
                                        <p className={styles.fieldDescription}>
                                            Choose which introducer sources to include in the valuation. You can select
                                            all introducers or choose specific ones.
                                        </p>
                                        <div className={styles.introducerModeSelector}>
                                            <button
                                                type="button"
                                                className={`${styles.presetButton} ${
                                                    introducerSelectionMode === 'all' ? styles.active : ''
                                                }`}
                                                onClick={() => setIntroducerSelectionMode('all')}
                                            >
                                                All Introducers
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.presetButton} ${
                                                    introducerSelectionMode === 'selected' ? styles.active : ''
                                                }`}
                                                onClick={() => setIntroducerSelectionMode('selected')}
                                            >
                                                Select Specific
                                            </button>
                                        </div>
                                        {introducerSelectionMode === 'selected' && (
                                            <div className={styles.introducerCheckboxes}>
                                                <div className={styles.introducerSelectActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.selectAllButton}
                                                        onClick={handleSelectAllIntroducers}
                                                    >
                                                        Select All
                                                    </button>
                                                </div>
                                                <div className={styles.introducerGrid}>
                                                    {AVAILABLE_INTRODUCERS.map((introducer) => (
                                                        <label
                                                            key={introducer.id}
                                                            className={styles.introducerCheckbox}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIntroducers.includes(introducer.id)}
                                                                onChange={() => handleIntroducerToggle(introducer.id)}
                                                            />
                                                            <span className={styles.checkboxLabel}>
                                                                {introducer.label}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {errors.introducers && (
                                            <span className={styles.errorText}>{errors.introducers}</span>
                                        )}
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="commission">
                                            Commission Percentage <span className={styles.required}>*</span>
                                        </label>
                                        <p className={styles.fieldDescription}>
                                            The percentage of the loan value to calculate commission. Example: where
                                            loan value is £10,000 and commission is 10%, the commission value = £1,000.
                                        </p>
                                        <input
                                            id="commission"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={commissionPercentage}
                                            onChange={(e) => setCommissionPercentage(e.target.value)}
                                            className={errors.commission ? styles.error : ''}
                                            placeholder="e.g. 10"
                                        />
                                        {errors.commission && (
                                            <span className={styles.errorText}>{errors.commission}</span>
                                        )}
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="statutory">
                                            Annual Statutory Interest Percentage{' '}
                                            <span className={styles.required}>*</span>
                                        </label>
                                        <p className={styles.fieldDescription}>
                                            The annual statutory interest rate used to calculate daily interest. This is
                                            divided by 365 days to get the daily rate, then applied to calculate total
                                            interest accrued.
                                        </p>
                                        <input
                                            id="statutory"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={statutoryInterestPercentage}
                                            onChange={(e) => setStatutoryInterestPercentage(e.target.value)}
                                            className={errors.statutory ? styles.error : ''}
                                            placeholder="e.g. 8"
                                        />
                                        {errors.statutory && (
                                            <span className={styles.errorText}>{errors.statutory}</span>
                                        )}
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="minClientValue">Minimum Client Value</label>
                                        <p className={styles.fieldDescription}>
                                            Optional: Set a minimum client value threshold. Clients whose total combined
                                            claim value is below this amount will be excluded from results and metrics.
                                        </p>
                                        <input
                                            id="minClientValue"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={minimumClientValue}
                                            onChange={(e) => setMinimumClientValue(e.target.value)}
                                            className={errors.minimumClientValue ? styles.error : ''}
                                            placeholder="e.g. 500"
                                        />
                                        {errors.minimumClientValue && (
                                            <span className={styles.errorText}>{errors.minimumClientValue}</span>
                                        )}
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="date">
                                            Calculation Date <span className={styles.required}>*</span>
                                        </label>
                                        <p className={styles.fieldDescription}>
                                            Select the date to calculate claims from. The total number of days from the
                                            claim start date to this date will be used to calculate statutory interest.
                                        </p>
                                        <div className={styles.datePresets}>
                                            {datePresets.map((preset) => (
                                                <button
                                                    key={preset.value}
                                                    type="button"
                                                    className={`${styles.presetButton} ${
                                                        selectedDate === preset.value ? styles.active : ''
                                                    }`}
                                                    onClick={() => setSelectedDate(preset.value)}
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                        </div>
                                        {errors.date && <span className={styles.errorText}>{errors.date}</span>}

                                        {selectedDate === 'custom' && (
                                            <div className={styles.customDateWrapper}>
                                                <input
                                                    type="date"
                                                    value={customDate}
                                                    onChange={(e) => setCustomDate(e.target.value)}
                                                    className={errors.customDate ? styles.error : ''}
                                                />
                                                {errors.customDate && (
                                                    <span className={styles.errorText}>{errors.customDate}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.submitWrapper}>
                                        <SubmitButton
                                            text="Calculate Client Values"
                                            btnType="primary"
                                            onClick={handleSubmit}
                                        />
                                    </div>
                                </form>

                                <div className={styles.calculationDetails}>
                                    <div
                                        className={styles.calculationHeader}
                                        onClick={() => setIsCalculationExpanded(!isCalculationExpanded)}
                                    >
                                        <span className={styles.headerText}>How is the claim value calculated?</span>
                                        <span
                                            className={`${styles.chevron} ${
                                                isCalculationExpanded ? styles.expanded : ''
                                            }`}
                                        >
                                            {isCalculationExpanded ? '−' : '+'}
                                        </span>
                                    </div>
                                    {isCalculationExpanded && (
                                        <div className={styles.calculationContent}>
                                            <div className={styles.calculationGrid}>
                                                <div className={styles.calculationStep}>
                                                    <span className={styles.stepLabel}>Step 1</span>
                                                    <h5>Daily Statutory Interest %</h5>
                                                    <p>Annual rate ÷ 365</p>
                                                    <code>8% ÷ 365 = 0.0219178% per day</code>
                                                </div>
                                                <div className={styles.calculationStep}>
                                                    <span className={styles.stepLabel}>Step 2</span>
                                                    <h5>Daily Interest as Decimal</h5>
                                                    <p>Daily % ÷ 100</p>
                                                    <code>0.0219178% ÷ 100 = 0.000219178</code>
                                                </div>
                                                <div className={styles.calculationStep}>
                                                    <span className={styles.stepLabel}>Step 3</span>
                                                    <h5>Daily Interest Value</h5>
                                                    <p>Commission value × Daily decimal</p>
                                                    <code>£1,000 × 0.000219178 = £0.219178</code>
                                                </div>
                                                <div className={styles.calculationStep}>
                                                    <span className={styles.stepLabel}>Step 4</span>
                                                    <h5>Total Statutory Interest</h5>
                                                    <p>Total days × Daily interest value</p>
                                                    <code>486 days × £0.219178 = £106.52</code>
                                                </div>
                                                <div className={styles.calculationStep}>
                                                    <span className={styles.stepLabel}>Step 5</span>
                                                    <h5>Final Claim Value</h5>
                                                    <p>Total statutory interest + Commission value</p>
                                                    <code>£106.52 + £1,000 = £1,106.52</code>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Results Section - Only show after calculation */}
            {hasCalculated && metrics && (
                <>
                    {/* Metrics Cards */}
                    <div className={styles.metricsContainer}>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>Total Book Value</span>
                            <span className={styles.metricValue}>{formatCurrency(metrics.totalBookValue)}</span>
                            <span className={styles.metricSubtext}>{formatNumber(metrics.totalClaims)} claims</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>Total Commission</span>
                            <span className={styles.metricValue}>{formatCurrency(metrics.totalCommissionValue)}</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>Total Statutory Interest</span>
                            <span className={styles.metricValue}>{formatCurrency(metrics.totalStatutoryInterest)}</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>Average Claim Value</span>
                            <span className={styles.metricValue}>{formatCurrency(metrics.averageClaimValue)}</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>Highest Claim</span>
                            <span className={styles.metricValue}>{formatCurrency(metrics.highestClaimValue)}</span>
                            {metrics.highestClaimRef && (
                                <span className={styles.metricSubtext}>{metrics.highestClaimRef}</span>
                            )}
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>Lowest Claim</span>
                            <span className={styles.metricValue}>{formatCurrency(metrics.lowestClaimValue)}</span>
                            {metrics.lowestClaimRef && (
                                <span className={styles.metricSubtext}>{metrics.lowestClaimRef}</span>
                            )}
                        </div>
                    </div>

                    {/* Search and Table */}
                    <div className={styles.resultsContainer}>
                        <div className={styles.resultsHeader}>
                            <h3>Client Valuations</h3>
                            <div className={styles.headerActions}>
                                <div className={styles.searchWrapper}>
                                    <input
                                        type="text"
                                        placeholder="Search by claim reference, client name or lender..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={handleSearchKeyPress}
                                        className={styles.searchInput}
                                    />
                                    <button
                                        className={styles.searchButton}
                                        onClick={handleSearch}
                                        disabled={isSearching || !searchTerm.trim()}
                                    >
                                        {isSearching ? 'Searching...' : 'Search'}
                                    </button>
                                    {searchResults !== null && (
                                        <button className={styles.clearSearchButton} onClick={handleClearSearch}>
                                            Clear
                                        </button>
                                    )}
                                </div>
                                <button className={styles.exportButton} onClick={handleExport} disabled={isExporting}>
                                    {isExporting ? 'Exporting...' : 'Export CSV'}
                                </button>
                            </div>
                        </div>

                        <div className={styles.tableWrapper}>
                            <table className={styles.resultsTable}>
                                <thead>
                                    <tr>
                                        <th>Claim Ref</th>
                                        <th>Client</th>
                                        <th>Email</th>
                                        <th>Lender</th>
                                        <th>Loan Value</th>
                                        <th>Commission</th>
                                        <th>Daily Interest %</th>
                                        <th>Daily Interest £</th>
                                        <th>Total Days</th>
                                        <th>Total Stat Interest</th>
                                        <th>Claim Value</th>
                                        <th>Client Total</th>
                                        <th>Client Claims</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayRecords.length > 0 ? (
                                        displayRecords.map((record, index) => (
                                            <tr key={`${record.claim_ref}-${index}`}>
                                                <td>{record.claim_ref}</td>
                                                <td>{record.client_name}</td>
                                                <td>{record.email}</td>
                                                <td>{record.lender}</td>
                                                <td>{formatCurrency(record.loan_value)}</td>
                                                <td>{formatCurrency(record.commission_value)}</td>
                                                <td>{formatPercentage(record.daily_stat_interest_percentage)}</td>
                                                <td>{formatCurrency(record.daily_stat_interest_value)}</td>
                                                <td>{formatNumber(record.total_days)}</td>
                                                <td>{formatCurrency(record.total_statutory_interest)}</td>
                                                <td className={styles.claimValueCell}>
                                                    {formatCurrency(record.claim_value)}
                                                </td>
                                                <td className={styles.clientTotalCell}>
                                                    {record.client_total_value
                                                        ? formatCurrency(record.client_total_value)
                                                        : '-'}
                                                </td>
                                                <td>{record.client_claim_count || '-'}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={13} className={styles.noResults}>
                                                {searchResults !== null
                                                    ? 'No claims found matching your search'
                                                    : 'No claim records available'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.tableFooter}>
                            <span>
                                {searchResults !== null
                                    ? `Found ${formatNumber(
                                          searchPagination?.totalRecords || displayRecords.length,
                                      )} matching claims`
                                    : `Showing ${formatNumber(claimRecords.length)} of ${formatNumber(
                                          pagination?.totalRecords || 0,
                                      )} claims`}
                            </span>
                            {pagination?.hasNextPage && searchResults === null && (
                                <button
                                    className={styles.loadMoreButton}
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? 'Loading...' : `Load More (${PAGE_SIZE} at a time)`}
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
